import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import DriverPicker, { driverOptionShape } from './DriverPicker';
import Input from '../../../components/atoms/forms/Input';
import Button from '../../../components/atoms/forms/Button';
import SegmentedToggle from '../../../components/atoms/SegmentedToggle';
import { SHIFT_FILTERS, VIEW_OPTIONS } from './mapConfig';

// One filter row shared by the list and both map modes. The parent owns every
// value; this bar only renders controls and forwards changes. `drivers` is
// one option per PERSON, built from the shift+location-scoped fleet, so the
// picker never offers someone the other filters have removed. Routes are a
// list-view idea: they are found through the search box (name, driver or
// stop) and opened from the list or a driver's day; the map is one driver's
// day, chosen with the picker alone, so the search box is hidden there.
const StyledDiv = styledComponent('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing.fieldGap,

  '& .mfb-filters': {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing.fieldGap,
  },
  '& .mfb-location': {
    width: theme.sizes.driverSelectWidth,
  },
});

function MonitoringFilterBar({
  drivers,
  selectedDriverEmail,
  showAllDrivers,
  onSelectDriver,
  selectedRouteId,
  locationQuery,
  onLocationQueryChange,
  shiftFilter,
  onShiftChange,
  viewMode,
  onViewModeChange,
  onClear,
}) {
  // selectedRouteId still informs Clear: a route opened from the list is an
  // active focus worth clearing, even with no picker to change it.
  const hasActiveFilter = Boolean(
    selectedDriverEmail ||
      showAllDrivers ||
      selectedRouteId ||
      locationQuery ||
      shiftFilter !== 'all',
  );

  return (
    <StyledDiv>
      <div className="mfb-filters">
        <DriverPicker
          drivers={drivers}
          selectedDriverEmail={selectedDriverEmail}
          showAllDrivers={showAllDrivers}
          onChange={onSelectDriver}
          align="left"
        />
        {viewMode === 'list' && (
          <div className="mfb-location">
            <Input
              type="search"
              value={locationQuery}
              onChange={(event) => onLocationQueryChange(event.target.value)}
              placeholder="Search routes"
              aria-label="Search routes by name, driver or stop"
              dataTestId="monitoring-location-filter"
            />
          </div>
        )}
        <SegmentedToggle
          options={SHIFT_FILTERS}
          value={shiftFilter}
          onChange={onShiftChange}
          ariaLabel="Filter routes by shift"
          size="sm"
        />
        {hasActiveFilter && (
          <Button
            variant="link"
            onClick={onClear}
            dataTestId="monitoring-clear-filters"
          >
            Clear
          </Button>
        )}
      </div>
      <SegmentedToggle
        options={VIEW_OPTIONS}
        value={viewMode}
        onChange={onViewModeChange}
        ariaLabel="Switch view"
      />
    </StyledDiv>
  );
}

MonitoringFilterBar.propTypes = {
  drivers: PropTypes.arrayOf(driverOptionShape).isRequired,
  selectedDriverEmail: PropTypes.string,
  showAllDrivers: PropTypes.bool,
  onSelectDriver: PropTypes.func.isRequired,
  selectedRouteId: PropTypes.string,
  locationQuery: PropTypes.string.isRequired,
  onLocationQueryChange: PropTypes.func.isRequired,
  shiftFilter: PropTypes.oneOf(['all', 'am', 'pm']).isRequired,
  onShiftChange: PropTypes.func.isRequired,
  viewMode: PropTypes.oneOf(['list', 'map']).isRequired,
  onViewModeChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

MonitoringFilterBar.defaultProps = {
  selectedDriverEmail: null,
  showAllDrivers: false,
  selectedRouteId: null,
};

export default MonitoringFilterBar;
